// @ts-nocheck

import React, { useState } from 'react';
import { Add } from '@mui/icons-material';
import { SelectChangeEvent, Skeleton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AtomAutocomplete from '@/components/atoms/Autocomplete';
import AtomButton from '@/components/atoms/Button';
import FloatingActionButton from '@/components/atoms/FloatingActionButton';
import AtomSelect from '@/components/atoms/Select';
import OrgDialog from '@/components/organisms/Dialog';
import OrgStudyCard from '@/components/organisms/StudyCard';
import RawTemplate from '@/components/templates/RawTemplate';
import { useSafeEffect } from '@/hooks/useSafeEffect';
import homeStyles from '@/pages/Home/home.module.css';
import { AppDispatch } from '@/store';
import { selectUser } from '@/store/auth/selectors';
import { resetYearSelected } from '@/store/studies';
import { getAllStudies } from '@/store/studies/selectors';
import { setStudyId, setStudyName } from '@/store/studies/slice';
import { studies } from '@/store/studies/thunks';
import { getUserStudies } from '@/store/user/selectors';
import { user } from '@/store/user/thunks';
import { UserStudy } from '@/types/user';

const Home = () => {
  const navigate = useNavigate();
  const userData = useSelector(selectUser);
  const studiesData = useSelector(getAllStudies);
  const userStudiesData = useSelector(getUserStudies);
  const dispatch = useDispatch<AppDispatch>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [role, setRole] = useState('');
  const [study, setStudy] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const roleOptions = [
    { label: 'Owner', value: 'owner' },
    { label: 'Secondary', value: 'secondary' },
    { label: 'Peer', value: 'peer' },
  ];
  const studiesOptions = studiesData.map(res => {
    return { label: res.study_name || res.name, value: res.id };
  });

  const fetchUserStudies = async () => {
    setIsLoading(true);
    try {
      await dispatch(user.getUserStudies({ userId: userData.id }));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllStudies = async () => {
    await dispatch(studies.getAllStudies());
  };

  const openDialog = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    setRole(event.target.value as string);
  };

  const studyhandleChange = (event: any, newValue: string | null) => {
    setStudy(newValue);
  };

  const goToStudyLanding = (studyData: UserStudy) => {
    dispatch(setStudyId(studyData.study_id));
    // Use study_name instead of name for the new API response structure
    dispatch(setStudyName(studyData.study_name || studyData.name));
    dispatch(resetYearSelected());
    navigate('/dashboard');
  };

  const LoadingState = () => (
    <>
      <div className={`${homeStyles.cards}`} style={{ background: 'white', padding: '24px' }}>
        {/* Title */}
        <Skeleton variant="text" width="80%" height={28} style={{ marginBottom: '16px' }} />

        {/* Percentage */}
        <Skeleton variant="text" width="30%" height={48} style={{ marginBottom: '8px' }} />

        {/* Company Count and Revenue */}
        <Skeleton variant="text" width="60%" height={20} style={{ marginBottom: '4px' }} />
        <Skeleton variant="text" width="70%" height={20} style={{ marginBottom: '24px' }} />

        {/* Donut Chart */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Skeleton variant="circular" width={200} height={200} />
        </div>
      </div>
      <div className={`${homeStyles.cards}`} style={{ background: 'white', padding: '24px' }}>
        {/* Title */}
        <Skeleton variant="text" width="80%" height={28} style={{ marginBottom: '16px' }} />

        {/* Percentage */}
        <Skeleton variant="text" width="30%" height={48} style={{ marginBottom: '8px' }} />

        {/* Company Count and Revenue */}
        <Skeleton variant="text" width="60%" height={20} style={{ marginBottom: '4px' }} />
        <Skeleton variant="text" width="70%" height={20} style={{ marginBottom: '24px' }} />

        {/* Donut Chart */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Skeleton variant="circular" width={200} height={200} />
        </div>
      </div>
      <div className={`${homeStyles.cards}`} style={{ background: 'white', padding: '24px' }}>
        {/* Title */}
        <Skeleton variant="text" width="80%" height={28} style={{ marginBottom: '16px' }} />

        {/* Percentage */}
        <Skeleton variant="text" width="30%" height={48} style={{ marginBottom: '8px' }} />

        {/* Company Count and Revenue */}
        <Skeleton variant="text" width="60%" height={20} style={{ marginBottom: '4px' }} />
        <Skeleton variant="text" width="70%" height={20} style={{ marginBottom: '24px' }} />

        {/* Donut Chart */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Skeleton variant="circular" width={200} height={200} />
        </div>
      </div>
    </>
  );

  const addStudyToUserDashboard = async () => {
    await dispatch(
      user.addUserStudies({
        user_id: userData.id,
        study_id: study.value,
        role: role,
      })
    );
    await fetchUserStudies();
    closeDialog();
    setRole('');
    setStudy(null);
  };

  // Add this function to handle delete request
  const handleDeleteStudy = async (studyId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click

    // Directly call the remove API without confirmation
    await dispatch(
      user.removeStudyFromUser({
        user_id: userData.id,
        study_id: studyId,
      })
    );

    // Refresh the list after deletion
    await fetchUserStudies();
  };

  useSafeEffect(async () => {
    await fetchUserStudies();
    await fetchAllStudies();
  }, [dispatch]);

  // Transform the data to provide backward compatibility for OrgStudyCard component
  const transformedUserStudies = userStudiesData.map(study => ({
    ...study,
    // Map the new structure to the old structure that OrgStudyCard expects
    name: study.study_name,
    progress: study.yoy_revenue_percentage || 0,
    number_of_companies:
      study.metrics?.reduce((total, metric) => total + (metric.count || 0), 0) || 0,
    total_market_size: study.revenue || 0,
    // Fix revenue in metrics to match old format expected by StudyCard tooltip
    metrics: study.metrics?.map(metric => ({
      ...metric,
      revenues: metric.revenue, // Map 'revenue' to 'revenues' for backward compatibility
    })),
  }));

  return (
    <RawTemplate>
      <div className={homeStyles.pageHeader}>
        <h1>Studies</h1>
      </div>
      <div className={homeStyles.container}>
        {isLoading ? (
          // Show 3 skeleton cards
          <LoadingState />
        ) : transformedUserStudies.length > 0 ? (
          // Show actual data with transformed properties
          transformedUserStudies.map((studyData, index) => (
            <OrgStudyCard
              onClick={() => goToStudyLanding(studyData)}
              className={homeStyles.cards}
              key={index}
              {...studyData}
              onDelete={e => handleDeleteStudy(studyData.study_id, e)}
            />
          ))
        ) : (
          // Empty state when no studies are available
          <div className={homeStyles.emptyState}>
            <div className={homeStyles.emptyStateContent}>
              <h2>No studies available</h2>
              <p>
                You don't have any studies yet. Click the + button to add a study to your projects.
              </p>
            </div>
          </div>
        )}
      </div>
      <FloatingActionButton
        icon={<Add />}
        tooltip="Add new studies"
        position="bottomRight"
        onClick={openDialog}
      />

      <OrgDialog
        open={isDialogOpen}
        onClose={closeDialog}
        title="Add a study to your projects"
        maxWidth="sm"
        // allowFullScreen={true}
      >
        <div className={homeStyles.roleDialogContent}>
          <AtomSelect
            name="simple-select"
            label="Role"
            value={role}
            onChange={handleChange}
            options={roleOptions}
            className={homeStyles.roleOption}
          />
          <AtomAutocomplete
            options={studiesOptions}
            label="Study"
            onChange={studyhandleChange}
            value={study}
            loading={false}
            error={false}
            className={homeStyles.studyOption}
            getOptionLabel={option => {
              if (!option) return '';
              return option.label || '';
            }}
          />
          <AtomButton variant="contained" onClick={addStudyToUserDashboard} disableElevation>
            Submit
          </AtomButton>
        </div>
      </OrgDialog>
    </RawTemplate>
  );
};

export default Home;
